'use client'

import { cn } from '../../lib/utils'
import { Button } from './button'
import type { LucideIcon } from 'lucide-react'

export interface TableEmptyStateProps {
  /** Icon to display */
  icon: LucideIcon
  /** Main title text */
  title: string
  /** Description text */
  description: string
  /** Action button text */
  actionLabel?: string
  /** Action button click handler */
  onAction?: () => void
  /** Optional className for custom styling */
  className?: string
  /** Icon for the action button */
  actionIcon?: LucideIcon
}

/**
 * Reusable empty state component for tables
 * Displays a centered message with icon and optional action button
 */
export function TableEmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  className,
  actionIcon: ActionIcon,
}: TableEmptyStateProps) {
  return (
    <div className={cn('py-6 px-8 md:px-12 text-center flex flex-col items-center justify-center', className)}>
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center">
        <Icon className="w-8 h-8 text-slate-400" />
      </div>
      <p className="text-lg font-semibold text-foreground mb-2">{title}</p>
      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">{description}</p>
      {actionLabel && onAction && (
        <Button
          onClick={onAction}
          className="bg-gradient-to-r from-primary to-primary/90 text-primary-foreground hover:opacity-90 shadow-lg shadow-primary/25 rounded-xl px-6"
        >
          {ActionIcon && <ActionIcon className="w-4 h-4 me-2" />}
          {actionLabel}
        </Button>
      )}
    </div>
  )
}
